import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { useApi } from "@/shared/utils/request";
import { logger } from "@/shared/utils/logger";
import { format, toZonedTime } from "date-fns-tz";
import { io } from "socket.io-client";
import { useAuth } from "@workos-inc/authkit-react";

export type Entry = {
  id: number;
  date: string;
  matter: string;
  matter_id?: string;
  matter_name?: string;
  client?: string;
  description: string;
  task: string;
  activity?: string;
  document_name?: string;
  duration: number;
  units_seconds?: number;
  start_time?: string;
  end_time?: string;
  application?: string;
  // Admin feedback fields
  approved?: boolean;
  needs_improvement?: boolean;
  attorney_feedback?: string | null;
};

export type Matter = {
  matter: string;
  matter_id: string;
  client: string;
  description: string;
  task_group: string;
  activity_group: string;
  is_recent?: number;
};

export type Task = {
  task: string;
  task_description: string;
  task_group: string;
};

export type Activity = {
  activity: string;
  activity_description: string;
  activity_group: string;
};

type DashboardDataContextType = {
  // Data
  entries: Entry[];
  exportedEntries: Entry[];
  matters: Matter[];
  tasks: Task[];
  activities: Activity[];

  // Loading states
  isLoading: boolean;
  isProcessingEntry: boolean;
  processingApplication: string | null;

  // Refresh functions
  refreshRecords: () => Promise<void>;
  refreshAll: () => Promise<void>;
};

const DashboardDataContext = createContext<DashboardDataContextType | undefined>(undefined);

export function DashboardDataProvider({ children }: { children: ReactNode }) {
  const { makeApiRequest } = useApi();
  const { user, isLoading: isAuthLoading } = useAuth();

  const [entries, setEntries] = useState<Entry[]>([]);
  const [exportedEntries, setExportedEntries] = useState<Entry[]>([]);
  const [matters, setMatters] = useState<Matter[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessingEntry, setIsProcessingEntry] = useState(false);
  const [processingApplication, setProcessingApplication] = useState<string | null>(null);

  function transformData(data: any[]): Entry[] {
    const formatInTimeZone = (parsedTime: Date, fmt: string, tz: string) =>
      format(toZonedTime(parsedTime, tz), fmt, { timeZone: tz });

    return (data || [])
      .map((record: any) => ({
        id: record.id,
        date: record.date
          ? formatInTimeZone(new Date(record.date), "MM/dd/yyyy", "UTC")
          : "",
        matter: record.matter || record.matter_name || "",
        matter_id: record.matter_id || "",
        matter_name: record.matter_name || "",
        client: record.client || "",
        description: record.description || "",
        task: record.task || "",
        activity: record.activity || "",
        document_name: record.document_name || "",
        duration: parseFloat(record.duration || record.total_hours) || 0,
        units_seconds: record.units_seconds || 0,
        start_time: record.start_time || "",
        end_time: record.end_time || "",
        application: record.application || "",
        // Admin feedback fields
        approved: record.approved,
        needs_improvement: record.needs_improvement,
        attorney_feedback: record.attorney_feedback,
      }))
      .sort((a, b) => {
        if (a.end_time && b.end_time) {
          return new Date(b.end_time).getTime() - new Date(a.end_time).getTime();
        }
        return 0;
      });
  }

  const refreshRecords = useCallback(async () => {
    try {
      const startTime = performance.now();

      const [recordsResponse, exportedResponse] = await Promise.all([
        makeApiRequest({ endpoint: "/records", method: "GET" }),
        makeApiRequest({ endpoint: "/records/exported", method: "GET" }),
      ]);

      const transformedRecords = transformData(recordsResponse.data || []);
      const transformedExported = transformData(exportedResponse.data || []);

      setEntries(transformedRecords);
      setExportedEntries(transformedExported);

      logger.debug("Records loaded", {
        recordCount: transformedRecords.length,
        exportedCount: transformedExported.length,
        durationMs: Math.round(performance.now() - startTime),
      });
    } catch (e) {
      if (e instanceof Error) {
        logger.captureException(e, { action: 'refreshRecords' });
      }
    }
  }, [makeApiRequest]);

  const refreshMetadata = useCallback(async () => {
    try {
      const startTime = performance.now();

      const [mattersResponse, tasksResponse, activitiesResponse] = await Promise.all([
        makeApiRequest({ endpoint: "/matters", method: "GET" }),
        makeApiRequest({ endpoint: "/tasks/allTasks", method: "GET" }),
        makeApiRequest({ endpoint: "/activities", method: "GET" }),
      ]);

      setMatters(mattersResponse.data || []);
      setTasks(tasksResponse.data || []);
      setActivities(activitiesResponse.data || []);

      logger.debug("Metadata loaded", {
        mattersCount: (mattersResponse.data || []).length,
        tasksCount: (tasksResponse.data || []).length,
        activitiesCount: (activitiesResponse.data || []).length,
        durationMs: Math.round(performance.now() - startTime),
      });
    } catch (e) {
      if (e instanceof Error) {
        logger.captureException(e, { action: 'refreshMetadata' });
      }
    }
  }, [makeApiRequest]);

  const refreshAll = useCallback(async () => {
    setIsLoading(true);
    try {
      await Promise.all([refreshRecords(), refreshMetadata()]);
    } finally {
      setIsLoading(false);
    }
  }, [refreshRecords, refreshMetadata]);

  // Initial data load - wait for authentication to complete
  useEffect(() => {
    if (!isAuthLoading && user) {
      refreshAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthLoading, user]);

  // WebSocket connection for real-time entry processing updates
  useEffect(() => {
    // set up websocket connection
    const subdomain = window.location.hostname.split(".")[0];
    let API_URL_BASE = `https://${subdomain}api.hourglass.law`;

    if (window.location.hostname.includes("bpmlaw")) {
      API_URL_BASE = `https://hourglassapi.bpmlaw.com`;
    } else if (window.location.hostname.includes("tysonmendes")) {
      API_URL_BASE = `https://hourglassapi.tysonmendes.com`;
    } else if (window.location.hostname.includes("pregodonnell")) {
      API_URL_BASE = `https://hourglassapi.pregodonnell.com`;
    } else if (window.location.hostname.includes("localhost")) {
      API_URL_BASE = `http://localhost:8000`;
    } else if (window.location.hostname.includes("app.hourglass.law")) {
      API_URL_BASE = `https://api.hourglass.law`;
    }
    const socket = io(API_URL_BASE);

    const loadRecords = async () => {
      try {
        await refreshRecords();
      } catch (error) {
        if (error instanceof Error) {
          logger.captureException(error, { action: 'loadRecordsWebSocket' });
        }
      }
    };

    socket.on("connect", () => {
      logger.debug("WebSocket connected");
    });

    socket.on("entry processing started", (data) => {
      if (data.email === user?.email) {
        setIsProcessingEntry(true);
        setProcessingApplication(data.application || null);
      }
    });

    socket.on("entry processing completed", (data) => {
      if (data.email === user?.email) {
        setIsProcessingEntry(false);
        setProcessingApplication(null);
        loadRecords();
      }
    });

    // Listen for admin feedback updates on attorney entries
    socket.on("entry_feedback_updated", (data) => {
      if (data.email === user?.email) {
        loadRecords();
      }
    });

    socket.on("connect_error", (error) => {
      logger.captureException(error, { action: 'websocketConnect' });
    });

    return () => {
      socket.off("connect");
      socket.off("entry processing started");
      socket.off("entry processing completed");
      socket.off("entry_feedback_updated");
      socket.off("connect_error");
      socket.disconnect();
    };
  }, []);

  const value: DashboardDataContextType = {
    entries,
    exportedEntries,
    matters,
    tasks,
    activities,
    isLoading,
    isProcessingEntry,
    processingApplication,
    refreshRecords,
    refreshAll,
  };

  return (
    <DashboardDataContext.Provider value={value}>
      {children}
    </DashboardDataContext.Provider>
  );
}

export function useDashboardData() {
  const context = useContext(DashboardDataContext);
  if (context === undefined) {
    throw new Error("useDashboardData must be used within a DashboardDataProvider");
  }
  return context;
}
